/**
 * Legacy -> unified-schema normalization for migrated search/listing blocks.
 *
 * The three archives were forked separately and spelled the same concepts
 * differently (status = TSPStatus / DirStatus / DelStatus; type = TSPType /
 * NNSAType / DelType; date = ApprovedDate / issueDate / effective; etc.), and
 * every block still references the old portal_type ids. Content was migrated
 * with absolute fidelity, so rather than rewrite ~90 blocks we normalize their
 * data at render time onto the new catalog indexes. This is the single place
 * the three-way variation is reconciled.
 */

/** legacy portal_type id -> unified content type */
export const PTYPE_MAP: Record<string, string> = {
  'TSP.Type.standard': 'Standard',
  'TSP.Type.project': 'Project',
  'TSP.Type.person': 'Person',
  'DOE.Directives.directive': 'Directive',
  'DOE.Directives.delegation': 'Delegation',
  'DOE.Directives.definition': 'Definition',
  'DOE.Directives.acronym': 'Acronym',
  'DOE.Directives.otherpolicy': 'OtherPolicy',
  'DOE.Directives.other': 'OtherPolicy',
  'DOE.Directives.charter': 'Charter',
  'NNSA.Type.policy': 'Policy',
  // short legacy FTI ids (directives glossary / nnsa charters)
  acronyms: 'Acronym',
  charters: 'Charter',
  'NNSA.Type.delegation': 'Delegation',
  'NNSA.Type.directive': 'Directive',
};

/** legacy index / metadata name -> unified catalog index/metadata name */
export const INDEX_MAP: Record<string, string> = {
  // status
  TSPStatus: 'status', DirStatus: 'status', DelStatus: 'status', status: 'status',
  // category / type
  TSPType: 'document_category', NNSAType: 'document_category', type: 'document_category',
  DelType: 'delegation_type',
  // classification
  opi: 'opi', OPI: 'opi',
  SeriesLines: 'series', series: 'series',
  subjectareas: 'subject_areas', subject_areas: 'subject_areas',
  DocID: 'docid', docid: 'docid', 'Doc ID': 'docid',
  // dates
  ApprovedDate: 'issue_date', issueDate: 'issue_date', issue_date: 'issue_date',
  ApprovedDateDel: 'authority_effective_date',
  effective: 'effective',
  updatedDate: 'updated_date', UpdatedDate: 'updated_date',
  supersededDate: 'supersedes_date', SupersededDate: 'supersedes_date',
  cancelsDate: 'cancels_date', CancelsDate: 'cancels_date',
  certifiedDate: 'certifies_date', certifiesDate: 'certifies_date',
  reaffirmsDate: 'reaffirms_date',
  effectiveDate: 'authority_effective_date',
  // booleans
  crd: 'crd', dnfsb: 'dnfsb', DNFSB: 'dnfsb', cta: 'cta', subpart: 'subpart',
  invokingDirective: 'invoking_directive', invokingdirective: 'invoking_directive',
  invokedstandard: 'invoked_standard',
  isDoeEmployee: 'is_doe_employee',
  // people / project
  Delegate: 'delegate', DelegatePos: 'delegate_position',
  Delegant: 'delegant', DelegantPos: 'delegant_position',
  slm: 'slm', slmOrg: 'slm_org', siteOrg: 'site_org',
  personRole: 'person_role', personStatus: 'person_status', email: 'email',
  // glossary
  categories: 'categories', termOPI: 'term_opi',
  firstLetterAcronym: 'first_letter', firstLetterTermDefinition: 'first_letter',
  acronym: 'acronym', sources: 'sources',
  // project
  ProjectStatus: 'project_status', Process: 'process',
  documentID: 'document_id', projectRegistrationNumber: 'registration_number',
  nextMilesstoneDescription: 'next_milestone_description',
  projectStartDate: 'project_start_date',
  // standards (capitalized variant)
  InvokedStandard: 'invoked_standard',
  // rarer spellings found only on non-public (draft/private) pages
  OPIList: 'opi', PersonRole: 'person_role', PersonStatus: 'person_status',
  slmName: 'slm', SiteOrg: 'site_org', nextMilestone: 'next_milestone',
  DirType: 'document_category', crdChoices: 'crd', IssueDate: 'issue_date',
  // keep-as-is (already unified / stock)
  Title: 'Title', sortable_title: 'sortable_title', getId: 'getId',
  relatedItems: 'relatedItems', path: 'path', portal_type: 'portal_type',
  review_state: 'review_state', Subject: 'Subject', Description: 'Description',
  Creator: 'Creator', CreationDate: 'CreationDate', ModificationDate: 'ModificationDate',
};

/** unified indexes that are booleans now; legacy queries send "Yes"/"No" */
export const BOOLEAN_INDEXES = new Set([
  'subpart', 'crd', 'dnfsb', 'cta', 'invoked_standard',
  'invoking_directive', 'is_doe_employee', 'redelegation_allowed',
]);
const mapBool = (v: any): any =>
  v === 'Yes' || v === 'yes' ? true : v === 'No' || v === 'no' ? false : v;

export const mapIndex = (name?: string): string =>
  typeof name === 'string' ? INDEX_MAP[name] || name : (name as any);
export const mapPtype = (v: string): string => PTYPE_MAP[v] || v;

/**
 * Normalize a search/listing block's data (query criteria, portal_type values,
 * facets, result fields, sort) from legacy names onto the unified schema.
 * Non-destructive: returns a shallow-cloned copy.
 */
export function normalizeBlockData(data: any): any {
  if (!data || typeof data !== 'object') return data;
  const d: any = { ...data };

  if (d.query && Array.isArray(d.query.query)) {
    d.query = {
      ...d.query,
      query: d.query.query.map((c: any) => {
        const i = mapIndex(c.i);
        let v = c.v;
        if (c.i === 'portal_type' && Array.isArray(v)) v = v.map(mapPtype);
        if (BOOLEAN_INDEXES.has(i)) v = Array.isArray(v) ? v.map(mapBool) : mapBool(v);
        return { ...c, i, v };
      }),
      sort_on: d.query.sort_on ? mapIndex(d.query.sort_on) : d.query.sort_on,
    };
  }

  // Which date field a block shows AND filters on depends on the family it
  // queries, not on what the legacy block happened to store. Computed here,
  // above the facets, so the facet and the column are repointed from ONE
  // decision. BL-0059 was precisely those two disagreeing: the column had been
  // moved to `issue_date` and the facet was left on `effective` while still
  // carrying the title "Issued".
  const ptypeCrit = (d.query?.query || []).find(
    (c: any) => c && c.i === 'portal_type',
  );
  const ptypes = Array.isArray(ptypeCrit?.v)
    ? ptypeCrit.v
    : ptypeCrit?.v != null
      ? [ptypeCrit.v]
      : [];

  /** Types that carry `issue_date`. Fill is total on the published corpus:
   *  Standard 599/600, Directive 1,762/1,762, Policy 387/387, OtherPolicy
   *  19/19 [catalog census, 2026-08-15]. */
  const ISSUE_DATE_TYPES = new Set([
    'Standard',
    'Directive',
    'Policy',
    'OtherPolicy',
  ]);

  const allOf = (t: string) =>
    ptypes.length > 0 && ptypes.every((p: any) => p === t);
  const allIssueDated =
    ptypes.length > 0 && ptypes.every((p: any) => ISSUE_DATE_TYPES.has(p));

  // Ordered: delegations first, because Delegation is not in
  // ISSUE_DATE_TYPES and must not fall through to the generic branch.
  const [EFF, EFF_TITLE] = allOf('Delegation')
    ? ['authority_effective_date', 'Effective Date']
    : allIssueDated
      ? ['issue_date', 'Issue Date']
      : ['effective', 'Effective Date'];

  const DATE_REPOINT = new Set([
    'issue_date',
    'authority_effective_date',
    'effective',
  ]);

  if (Array.isArray(d.facets)) {
    d.facets = d.facets.map((f: any) => {
      if (!f || !f.field || typeof f.field.value !== 'string') return f;
      const value = mapIndex(f.field.value);
      // A date facet is repointed exactly as the date column is, and takes the
      // same title, so the control cannot name one field and filter another.
      // Measured before this existed: the directives hub's control was titled
      // "Issued" and filtered `effective`, so asking for 2020 returned 23
      // documents of which 14 were issued in some other year -- one of them in
      // 1981, with the ISSUE DATE column on the same row saying so.
      if (DATE_REPOINT.has(value)) {
        return {
          ...f,
          field: { ...f.field, value: EFF, label: EFF_TITLE },
          title: EFF_TITLE,
        };
      }
      return { ...f, field: { ...f.field, value } };
    });
  }

  // --- Bind page/preset filters as SELECTED VALUES in the facet widgets ---
  // When the block's BASE QUERY constrains a field to MORE THAN ONE value
  // (e.g. the glossary family: portal_type in [Definition, Acronym]), a
  // single-select facet widget cannot display both — react-select renders an
  // array as a scalar `value` and shows nothing. Promote such a facet to
  // `multiple: true` so the applied values render as removable chips (Image
  // #1). The Facets.jsx shadow seeds the widget's value from the base query
  // when the user hasn't touched it (and unwraps length-1 arrays for the
  // single-select facets). Query semantics are unchanged: seeding is
  // display-only, and valueToQuery runs only on explicit user interaction.
  if (Array.isArray(d.facets) && d.query && Array.isArray(d.query.query)) {
    const baseVals: Record<string, any> = {};
    for (const c of d.query.query) {
      if (c && typeof c.i === 'string') baseVals[c.i] = c.v;
    }
    d.facets = d.facets.map((f: any) => {
      const fld = f?.field?.value;
      if (!fld) return f;
      const bv = baseVals[fld];
      if (Array.isArray(bv) && bv.length > 1 && !f.multiple) {
        return { ...f, multiple: true };
      }
      return f;
    });
  }

  if (Array.isArray(d.fields)) {
    d.fields = d.fields.map((f: any) => {
      // legacy blocks spell the column field three ways: 'name' (standards),
      // ['name'] (directives), {label, value} (facet-style)
      // a list with several names is a legacy multi-field cell (e.g.
      // [Delegate, DelegatePos]) and stays a list, each name mapped
      const raw = Array.isArray(f.field)
        ? f.field.length === 1
          ? f.field[0]
          : f.field.map((n: any) => (typeof n === 'string' ? mapIndex(n) : n))
        : f.field && typeof f.field === 'object'
          ? f.field.value
          : f.field;
      const sraw = Array.isArray(f.sortable) ? f.sortable[0] : f.sortable;
      const field = typeof raw === 'string' ? mapIndex(raw) : raw;
      return {
        ...f,
        field,
        sortable: typeof sraw === 'string' ? mapIndex(sraw) : sraw,
        // status column renders as a pp-pill (renderValue format:'status');
        // never override an explicitly configured format
        format: !f.format && field === 'status' ? 'status' : f.format,
      };
    });
  }

  // --- The date column on document/delegation browse pages ---
  //
  // Every such table gets ONE sortable date column: repoint whichever date
  // column the legacy block carried, and append one where a doc-like table
  // (has status/docid/category) shows none. Glossary/People (no status/docid)
  // are untouched.
  //
  // WHICH date is the whole question, and it is decided per family:
  //
  //   `issue_date`                 the date DOE asserts. Authoritative, and the
  //                                only field `prc-maintenance/content-model/
  //                                dates.md` permits for display.
  //   `authority_effective_date`   delegations only, and they carry NO
  //                                issue_date at all (0 of 982 directives, 0 of
  //                                55 nnsa) — so this is their authoritative
  //                                field, populated 982/982 and 55/55
  //                                [verified 2026-07-20, re-verified 2026-08-15].
  //   `effective`                  Plone bookkeeping. Kept only as the fallback
  //                                for a family we have not measured.
  //
  // This block used to repoint `issue_date` -> `effective` for every
  // non-delegation table, which inverted the rule above and made `/documents`
  // — the most-viewed page on the property — show a date more than a year from
  // the document's own on 977 published documents: standards 465/600 (77.5%),
  // nnsa 249/387 (64.3%), directives 263/1,762 (14.9%). `DOE-STD-1153-2002`
  // read "Nov 28, 2016" on the hub and "Approved Date: Aug 14, 2002" on its own
  // page. The `effective` values are a bulk load into the PREVIOUS portal
  // (standards cluster on 2016-11/2017-01, nnsa on 2017-2019); directives
  // largely escaped it at 74% exact, which is why a spot check there kept
  // confirming the wrong assumption. See BL-0054.
  //
  // Single field, single header — no per-row fallback — because every rendered
  // document browse BLOCK is single-family: of the 6 in the corpus, 3 are
  // Delegation-only, 1 Standard, 1 ['Directive','OtherPolicy'], 1 Policy, and
  // ZERO mix a type carrying issue_date with one that does not
  // [block scan, 2026-08-15]. Presets never restate portal_type, so a
  // `?preset=` deep link inherits its host block's family. If that ever stops
  // being true the `effective` fallback below is what a mixed table gets.
  //
  // The Search App is the exception, and it is not a block in that scan: it is
  // assembled in code from `search-registry.json` over all 9 searchable types,
  // so it IS mixed and it does need the per-row fallback. BL-0057 gave it one —
  // a column declaring `['issue_date','authority_effective_date']` with
  // `resolve: 'first'`. This function must leave that column alone rather than
  // flatten it back to `effective`; see `fieldNames` below.
  if (Array.isArray(d.fields) && d.fields.length) {
    /**
     * A column's field names, whether it declares one or a list.
     *
     * BL-0057 gave the Search App a date column declaring BOTH `issue_date`
     * and `authority_effective_date` with `resolve: 'first'`, because /search
     * is the one genuinely mixed table. Reading `f.field` as a scalar treats
     * that column as if it were not a date column at all — which is exactly
     * what happened: the repoint below skipped it (correctly), and then the
     * `hasEff` guard also missed it, so a SECOND date column got appended and
     * /search shipped both "Date" and "Effective" side by side.
     */
    const fieldNames = (f: any): string[] =>
      Array.isArray(f?.field) ? f.field : f?.field != null ? [f.field] : [];

    d.fields = d.fields.map((f: any) => {
      if (!f) return f;
      // Only a SINGLE-field date column is repointed. A column that declares a
      // list has already stated how it resolves and must be left as authored;
      // rewriting it to one field would silently undo that decision.
      if (!Array.isArray(f.field) && DATE_REPOINT.has(f.field)) {
        return {
          ...f,
          field: EFF,
          sortable: EFF,
          title: EFF_TITLE,
          format: f.format || 'date',
        };
      }
      return f;
    });
    // "Does this table already show a date?" -- not "does it show EFF?". Any
    // column reading a date field counts, however it is declared.
    const hasEff = d.fields.some((f: any) =>
      fieldNames(f).some((n: string) => DATE_REPOINT.has(n)),
    );
    const isDocTable = d.fields.some(
      (f: any) =>
        f &&
        (f.field === 'status' ||
          f.field === 'docid' ||
          f.field === 'document_category'),
    );
    if (isDocTable && !hasEff) {
      d.fields.push({
        field: EFF,
        title: EFF_TITLE,
        format: 'date',
        sortable: EFF,
        align: 'left',
      });
    }
  }

  if (typeof d.sortOn === 'string') d.sortOn = mapIndex(d.sortOn);
  return d;
}

export default normalizeBlockData;
